import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { getCalendarBlocks } from '../../services/calendarBlocks';
import { getAppointments } from '../../services/appointments';
import { sendLeadNotification } from '../../services/messaging/leadNotification';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  packageName?: string;
}

export default function QuoteRequestModal({ isOpen, onClose, packageName }: Props) {
  const [formData, setFormData] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    event_type: '',
    event_date: '',
    city: '',
    notes: '',
    package_interest: packageName || ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [blockedDates, setBlockedDates] = useState<string[]>([]);
  const [bookedDates, setBookedDates] = useState<string[]>([]);
  const [isDateUnavailable, setIsDateUnavailable] = useState(false);
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);
  const [filteredCities, setFilteredCities] = useState<string[]>([]);

  const californiaCities = [
    'Los Angeles',
    'San Francisco',
    'San Diego',
    'Sacramento',
    'San Jose',
    'Fresno',
    'Oakland',
    'Santa Ana',
    'Anaheim',
    'Riverside',
    'Bakersfield',
    'Stockton',
    'Irvine',
    'Chula Vista',
    'Fremont',
    'Santa Clarita',
    'Modesto',
    'Oxnard',
    'Fontana',
    'Moreno Valley',
    'Glendale',
    'Huntington Beach',
    'Santa Rosa',
    'Oceanside',
    'Garden Grove',
    'Ontario',
    'Elk Grove',
    'Corona',
    'Lancaster',
    'Palmdale',
    'Salinas',
    'Hayward',
    'Sunnyvale',
    'Pasadena',
    'Torrance',
    'Escondido',
    'Orange',
    'Fullerton',
    'Thousand Oaks',
    'Visalia',
  ];

  useEffect(() => {
    if (isOpen) {
      loadBlockedAndBookedDates();
    }
  }, [isOpen]);

  const loadBlockedAndBookedDates = async () => {
    try {
      const [blocks, appointments] = await Promise.all([
        getCalendarBlocks(),
        getAppointments()
      ]);
      
      setBlockedDates(blocks.map(b => b.date));
      setBookedDates(
        appointments
          .filter(a => a.status !== 'canceled')
          .map(a => a.date)
      );
    } catch (err) {
      console.error('Error loading dates:', err);
    }
  };

  const isDateDisabled = (dateStr: string) => {
    return blockedDates.includes(dateStr) || bookedDates.includes(dateStr);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validar fecha si fue seleccionada
    if (formData.event_date && isDateDisabled(formData.event_date)) {
      setError('La fecha seleccionada no está disponible. Por favor selecciona otra fecha.');
      return;
    }
    
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const { error: insertError } = await supabase
        .from('leads')
        .insert([{
          name: formData.client_name,
          email: formData.client_email || null,
          phone: formData.client_phone || null,
          event_type: formData.event_type || null,
          event_date: formData.event_date || null,
          city: formData.city || null,
          message: formData.notes || null,
          package_interest: formData.package_interest || null,
          status: 'new',
          source: 'website_packages'
        }]);

      if (insertError) throw insertError;

      // Notificar al admin por email (en background, no bloquea al usuario)
      sendLeadNotification({
        name: formData.client_name,
        email: formData.client_email,
        phone: formData.client_phone,
        message: formData.notes,
        event_type: formData.event_type,
        event_date: formData.event_date,
        city: formData.city,
      }).catch(err => {
        console.error('Error enviando notificación de lead:', err);
      });

      setSuccess(true);
      setFormData({
        client_name: '',
        client_email: '',
        client_phone: '',
        event_type: '',
        event_date: '',
        city: '',
        notes: '',
        package_interest: packageName || ''
      });

      setTimeout(() => {
        onClose();
        setSuccess(false);
      }, 3000);
    } catch (err: any) {
      setError(err.message || 'Error al enviar la solicitud. Por favor intenta de nuevo.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (name === 'event_date') {
      const isUnavailable = blockedDates.includes(value) || bookedDates.includes(value);
      setIsDateUnavailable(isUnavailable);
    }

    if (name === 'city') {
      const filtered = californiaCities.filter(city =>
        city.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredCities(filtered);
      setShowCitySuggestions(value.length > 0 && filtered.length > 0);
    }
  };

  const handleCitySelect = (city: string) => {
    setFormData(prev => ({ ...prev, city }));
    setShowCitySuggestions(false);
  };

  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <h3 className="text-2xl font-bold text-gray-900">Solicitar Cotización</h3>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors"
          >
            <i className="ri-close-line text-2xl text-gray-600"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {packageName && (
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
              <p className="text-sm text-teal-800">
                <i className="ri-information-line mr-2"></i>
                Cotización para: <strong>{packageName}</strong>
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="client_name" className="block text-sm font-medium text-gray-900 mb-2">
                Nombre completo *
              </label>
              <input
                type="text"
                id="client_name"
                name="client_name"
                value={formData.client_name}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white"
                placeholder="Tu nombre"
              />
            </div>

            <div>
              <label htmlFor="client_email" className="block text-sm font-medium text-gray-900 mb-2">
                Email *
              </label>
              <input
                type="email"
                id="client_email"
                name="client_email"
                value={formData.client_email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white"
                placeholder="tu@email.com"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="client_phone" className="block text-sm font-medium text-gray-900 mb-2">
                Teléfono *
              </label>
              <input
                type="tel"
                id="client_phone"
                name="client_phone"
                value={formData.client_phone}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white"
                placeholder="(555) 123-4567"
              />
            </div>

            <div>
              <label htmlFor="event_type" className="block text-sm font-medium text-gray-900 mb-2">
                Tipo de evento *
              </label>
              <select
                id="event_type"
                name="event_type"
                value={formData.event_type}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white"
              >
                <option value="">Selecciona...</option>
                <option value="Boda">Boda</option>
                <option value="Quinceañera">Quinceañera</option>
                <option value="Evento Corporativo">Evento Corporativo</option>
                <option value="Sesión Familiar">Sesión Familiar</option>
                <option value="Graduación">Graduación</option>
                <option value="Otro">Otro</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="event_date" className="block text-sm font-medium text-gray-900 mb-2">
                Fecha del evento
              </label>
              <input
                type="date"
                id="event_date"
                name="event_date"
                value={formData.event_date}
                onChange={handleChange}
                min={getMinDate()}
                className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white"
              />
              {formData.event_date && isDateDisabled(formData.event_date) && (
                <p className="text-sm text-red-600 mt-2 flex items-center gap-2">
                  <i className="ri-error-warning-fill"></i>
                  Esta fecha no está disponible. Por favor selecciona otra.
                </p>
              )}
            </div>

            <div className="relative">
              <label htmlFor="city" className="block text-sm font-medium text-gray-900 mb-2">
                Ciudad del evento <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                onFocus={() => {
                  if (formData.city.length > 0) {
                    const filtered = californiaCities.filter(city =>
                      city.toLowerCase().includes(formData.city.toLowerCase())
                    );
                    setFilteredCities(filtered);
                    setShowCitySuggestions(filtered.length > 0);
                  }
                }}
                onBlur={() => {
                  setTimeout(() => setShowCitySuggestions(false), 200);
                }}
                placeholder="Escribe tu ciudad..."
                required
                className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white"
              />
              {showCitySuggestions && filteredCities.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {filteredCities.map((city, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => handleCitySelect(city)}
                      className="w-full px-4 py-2 text-left hover:bg-teal-50 transition-colors text-sm text-gray-700 hover:text-teal-700"
                    >
                      {city}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div>
            <label htmlFor="notes" className="block text-sm font-medium text-gray-900 mb-2">
              Cuéntanos sobre tu evento
            </label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={4}
              maxLength={500}
              className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none bg-white"
              placeholder="Número de invitados, ubicación, detalles especiales..."
            />
            <p className="text-xs text-gray-500 mt-1">
              {formData.notes.length}/500 caracteres
            </p>
          </div>

          {success && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
              <i className="ri-checkbox-circle-fill text-xl text-green-600"></i>
              <p className="text-sm text-green-800">
                ¡Solicitud enviada! Te contactaremos pronto con tu cotización.
              </p>
            </div>
          )}

          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
              <i className="ri-error-warning-fill text-xl text-red-600"></i>
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium whitespace-nowrap"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || (formData.event_date && isDateDisabled(formData.event_date))}
              className="flex-1 px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {loading ? 'Enviando...' : 'Enviar Solicitud'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
